import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { Loader2, LogOut, Search, Eye, Headset, User, Wallet, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentDashboard = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const token = localStorage.getItem('mojo_agent_token');
  const agent = JSON.parse(localStorage.getItem('mojo_agent_user') || '{}');
  const headers = { Authorization: `Bearer ${token}` };

  // Phase 0 — agent session token in HttpOnly cookie; gate on profile.
  const isLoggedIn = !!agent?.id;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/agent/login');
      return;
    }
  }, []);

  const handleSearch = async () => {
    if (!search.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, limit: 20 });
      const response = await axios.get(`${API}/agent/accounts?${params}`, { headers });
      setAccounts(response.data.accounts || []);
      if ((response.data.accounts || []).length === 0) {
        toast.info('No accounts found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (accountId) => {
    try {
      const response = await axios.get(`${API}/agent/accounts/${accountId}`, { headers });
      setCurrentAccount(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Failed to fetch account details');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mojo_agent_token');
    localStorage.removeItem('mojo_agent_user');
    toast.success('Logged out');
    navigate('/agent/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5F0] via-[#F5EDE4] to-[#F5E8D8]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2B10C]/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#F2B10C] rounded-full flex items-center justify-center">
              <Headset size={20} className="text-[#1B0C34]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1B0C34]">Agent Dashboard</h1>
              <p className="text-xs text-[#145539]">{agent.full_name || agent.email}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm" className="border-[#F2B10C]">
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 space-y-6">

        {/* Search */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Lookup</CardTitle>
            <CardDescription>Search by name, email, or phone number</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Email, name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                <span className="ml-2">Search</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {accounts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Results</CardTitle>
              <CardDescription>{accounts.length} account{accounts.length !== 1 ? 's' : ''} found</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader className="bg-[#F5EDE4]">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{account.full_name}</TableCell>
                        <TableCell className="text-sm">{account.email}</TableCell>
                        <TableCell className="text-sm">{account.phone_number}</TableCell>
                        <TableCell>
                          <Badge variant={account.user_type === 'merchant' ? 'default' : 'secondary'}>
                            {account.user_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={account.status === 'active' ? 'default' : 'destructive'}>
                            {account.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => handleViewDetails(account.id)}>
                            <Eye size={14} className="mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Account Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-[460px]">
          <SheetHeader>
            <SheetTitle>Account Details</SheetTitle>
            <SheetDescription>{currentAccount?.user?.email}</SheetDescription>
          </SheetHeader>

          {currentAccount && (
            <div className="space-y-6 mt-6">

              {/* User Info */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <User size={16} className="text-[#1B0C34]" />
                  <h3 className="font-semibold">User Information</h3>
                </div>
                <div className="space-y-2 text-sm">
                  {[
                    ['Full Name', currentAccount.user.full_name],
                    ['Email', currentAccount.user.email],
                    ['Phone', currentAccount.user.phone_number],
                    ['ID Number', currentAccount.user.id_number || 'N/A'],
                    ['Province', currentAccount.user.province || 'N/A'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Type</span>
                    <Badge variant={currentAccount.user.user_type === 'merchant' ? 'default' : 'secondary'}>
                      {currentAccount.user.user_type}
                    </Badge>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Status</span>
                    <Badge variant={currentAccount.user.status === 'active' ? 'default' : 'destructive'}>
                      {currentAccount.user.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between py-1 border-b border-gray-100">
                    <span className="text-gray-500">Verified</span>
                    <Badge variant={currentAccount.user.biometric_verified ? 'default' : 'secondary'}>
                      {currentAccount.user.biometric_verified ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Wallet */}
              {currentAccount.wallet && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet size={16} className="text-[#1B0C34]" />
                    <h3 className="font-semibold">Wallet</h3>
                  </div>
                  <div className="bg-[#F5EDE4] rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Wallet ID</span>
                      <span className="font-mono text-xs">{currentAccount.wallet.wallet_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Balance</span>
                      <span className="font-bold text-lg text-[#145539]">
                        R{Number(currentAccount.wallet.balance || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">ACE Tokens</span>
                      <span className="font-medium">{Number(currentAccount.wallet.ace_token_balance || 0).toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status</span>
                      <Badge variant={currentAccount.wallet.status === 'active' ? 'default' : 'destructive'}>
                        {currentAccount.wallet.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Transactions */}
              {currentAccount.recent_transactions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <ArrowUpDown size={16} className="text-[#1B0C34]" />
                    <h3 className="font-semibold">Recent Transactions</h3>
                  </div>
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {currentAccount.recent_transactions.map((tx, idx) => (
                      <div key={idx} className="border rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium capitalize">{tx.type?.replace(/_/g, ' ')}</span>
                          <span className="font-bold text-[#145539]">R{Number(tx.amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{new Date(tx.created_at).toLocaleString()}</span>
                          <Badge variant="outline" className="text-xs">{tx.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AgentDashboard;
